import { FormConditionItem } from "@types";
import { useFormikContext } from "formik";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AnySchema, ObjectShape } from "yup";

export type GroupState = { [key: string]: boolean };
export type FormContextType = {
  groups: GroupState;
  // eslint-disable-next-line no-unused-vars
  validate: (name: string, value: any) => string;
};
const initialState: FormContextType = {
  groups: {},
  validate: () => {
    return "";
  },
};

const FormContext = createContext(initialState);

FormContext.displayName = "FormContext";

interface Props {
  conditions: FormConditionItem[];
  validationSchema: ObjectShape;
  children: React.ReactNode;
}

const FormProvider: React.FC<Props> = ({
  children,
  conditions,
  validationSchema,
}) => {
  const [groups, setGroups] = useState<GroupState>({});
  const setGroupEnable = useCallback(
    (name: string, enable: boolean) =>
      setGroups((prev) => ({ ...prev, [name]: enable })),
    [],
  );
  const { values } = useFormikContext<any>();

  const validate = useCallback(
    (name: string, value: any) => {
      const schema = validationSchema[name] as AnySchema;
      if (schema) {
        try {
          schema.validateSync(value);
        } catch (error) {
          return (error as any).message;
        }
      }
      // return "";
    },
    [validationSchema],
  );

  useEffect(() => {
    conditions.forEach((f) => {
      const key = f.if_field;
      const value = values[key];
      const isEqual = Array.isArray(value)
        ? value.includes(f.if_value)
        : value === f.if_value;

      setGroupEnable(
        f.then_field,
        (f.operator === "equals" && isEqual) ||
          (f.operator === "not_equals" && !isEqual),
      );
    });
  }, [values, conditions, setGroupEnable]);

  const value = useMemo(
    () => ({
      groups,
      setGroupEnable,
      validate,
    }),
    [groups, setGroupEnable, validate],
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export function useFormContext() {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error(`useFormContext must be used within a FormProvider`);
  }
  return context;
}

export default FormProvider;
